/**
 * Settings → AI: pure derivations for the page (no fetching, no React).
 *
 * Health comes from the most recent RESULT, never from whether a key is set —
 * a key can be present and still be rejected by the provider.
 */

export type AiHealth = 'healthy' | 'failing' | 'not_configured' | 'untested';

interface HealthInput {
  configured: boolean;
  last_success_at: number | null;
  last_error_at: number | null;
  breaker_open_until?: number | null;
}

export function deriveHealth(p: HealthInput): AiHealth {
  if (!p.configured) return 'not_configured';
  // The server reports breaker_open_until only while the breaker IS open.
  if (p.breaker_open_until) return 'failing';
  const ok = p.last_success_at ?? 0;
  const err = p.last_error_at ?? 0;
  if (!ok && !err) return 'untested';
  return ok >= err ? 'healthy' : 'failing';
}

export const HEALTH_LABEL: Record<AiHealth, string> = {
  healthy: 'Healthy',
  failing: 'Failing',
  not_configured: 'Not configured',
  untested: 'Untested',
};

interface FailureInput {
  kind?: string | null;
  status?: number | null;
  type?: string | null;
  message?: string | null;
}

/** One human line for a provider failure; the raw message goes behind "Details". */
export function summarizeFailure(f: FailureInput, provider: string): string {
  const msg = (f.message ?? '').toLowerCase();
  if (msg.includes('not scoped to a workspace')) return 'Key not scoped to a workspace';
  const code = f.status ? ` (${f.status})` : '';
  switch (f.kind) {
    case 'auth': return `Key rejected by ${provider}`;
    case 'quota': return 'Out of credit or over the spending cap';
    case 'rate_limited': return 'Rate limited';
    case 'timeout': return 'Timed out';
    case 'network': return `Could not reach ${provider}`;
    case 'server': return `${provider} had a server error${code}`;
    case 'schema_invalid': return 'Answered in the wrong format';
    case 'bad_request': return `Request refused${code}`;
    case 'breaker_open': return 'Paused after repeated failures';
    case 'disabled_env': return 'Blocked by AI_ASSIST_ENABLED';
    case 'not_configured': return 'No API key configured';
    default: return f.kind ? `Failed (${f.kind})` : 'Failed';
  }
}

export type AssistanceState = 'on' | 'off' | 'blocked' | 'unavailable';

/** The master row's single status line, derived from the env switch AND the setting. */
export function assistanceStatus(d: { env_enabled: boolean; env_killed?: boolean; enabled: boolean; store_ok?: boolean; any_configured?: boolean; any_runnable?: boolean }): { state: AssistanceState; text: string; hint?: string } {
  if (d.env_killed) {
    return {
      state: 'blocked',
      text: 'Blocked — AI_ASSIST_ENABLED is off in Vercel',
      hint: 'It is set to false, the hard kill switch: nothing is sent to either provider, including Test connection.',
    };
  }
  if (!d.env_enabled) {
    return {
      state: 'blocked',
      text: 'Blocked — AI_ASSIST_ENABLED is off in Vercel',
      hint: 'Set AI_ASSIST_ENABLED=true in the Vercel environment to allow it. Test connection still works.',
    };
  }
  if (!d.enabled) return { state: 'off', text: 'Off — switched off here' };
  // Switched on, but the router refuses every request without shared state.
  if (d.store_ok === false) {
    return { state: 'unavailable', text: 'Unavailable — shared state (Redis) is down', hint: 'Assistance resumes on its own when it recovers.' };
  }
  // The router skips providers without a key; with none left nothing runs.
  if (d.any_configured === false) {
    return { state: 'unavailable', text: 'Unavailable — no provider has an API key', hint: 'Add a key in the Vercel environment for at least one provider.' };
  }
  if (d.any_runnable === false) {
    return { state: 'unavailable', text: 'Unavailable — every provider is paused after repeated failures', hint: 'A passing Test connection resumes a provider.' };
  }
  return { state: 'on', text: 'On — suggestions active' };
}

export type AiRole = 'primary' | 'backup' | 'unused';

/** Providers in the order requests are routed: primary, then backup (or the unused one). */
export function routingOrder<T extends { id: string }>(providers: T[], primary: string, backup: string | null) {
  const byId = new Map(providers.map((p) => [p.id, p]));
  const rows: { provider: T; role: AiRole }[] = [];
  const first = byId.get(primary);
  if (first) rows.push({ provider: first, role: 'primary' });
  for (const p of providers) {
    if (p.id === primary) continue;
    rows.push({ provider: p, role: p.id === backup ? 'backup' : 'unused' });
  }
  return rows.sort((a, b) => ['primary', 'backup', 'unused'].indexOf(a.role) - ['primary', 'backup', 'unused'].indexOf(b.role));
}
